import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronDown } from 'lucide-react';

import {
  MIN_TREND_POINTS,
  TREND_DAYS,
  TREND_WINDOWS,
  buildTrendSeries,
} from './trendSeries';
import { ScoreHero, SectionHeadCard } from '../../components';

import type { TrendSeries, TrendWindow } from './trendSeries';
import type { DiagnosisRecord } from '../onboarding/record';

/**
 * 최근 보컬 추세(시안 `1014:9946` §7.1) — 종합 점수 하나의 꺾은선 + 선 아래 연분홍 면적.
 * 손수 SVG(차트 의존성 0 — 플랜 Boundaries).
 *
 * 축은 **종합 하나**다. 축 토글(호흡/음역/음정)은 20260730 안의 요소라 정본(20260729)에
 * 없다. 시계열 정본(`buildTrendSeries`)의 축 매개변수는 그대로 두고 여기서 `overall`만 넘긴다.
 *
 * 결측일은 **끊는다**. 0으로 이으면 안 잰 날이 "0점이던 날"이 되고, 그건 저장 계층이 지킨
 * "없으면 필드가 없다" 규약을 화면에서 뒤집는 것이다. 면적도 구간마다 따로 닫는다.
 */

interface TrendChartProps {
  /** 저장소가 내는 그대로. 이 컴포넌트는 저장소를 읽지 않는다(홈이 한 번만 읽는다). */
  records: DiagnosisRecord[];
  /** epoch ms — 창의 오른쪽 끝. 호출자가 준다(셀렉터와 같은 규약). */
  now: number;
}

const W = 339;
const H = 172;
/** 시안 `1016:10419` — 왼쪽 눈금 글자 22px + 간격 20px, 위 8px, 아래 날짜 줄 24px.
 *  오른쪽 여백은 눈금이 아니라 **마지막 날짜 글자**가 정한다 — 가운데 정렬이라
 *  절반이 마지막 점 오른쪽으로 나가고, viewBox를 넘으면 SVG가 그걸 잘라낸다. */
const PAD = { l: 42, r: 15, t: 8, b: 24 };

/** 칸 수는 창 길이가 정한다 — 7일과 30일이 같은 폭을 나눠 쓴다. */
/** 가로 눈금 — 시안 `1016:10420`의 100 · 50 · 0. `series.scale.ticks`가 더 촘촘해도 셋만 그린다. */
const GRID_TICKS = [100, 50, 0];

const xAt = (i: number, count: number): number =>
  PAD.l + (i * (W - PAD.l - PAD.r)) / Math.max(1, count - 1);

/** 눈금 밖 값도 그림 안에 머문다 — 텍스트 대안은 클램프 전 원값을 읽는다. */
const yAt = (val: number, max: number): number => {
  const clamped = Math.min(Math.max(val, 0), max);
  return PAD.t + (1 - clamped / max) * (H - PAD.t - PAD.b);
};

/** 날짜 라벨을 찍을 칸인가 — 시안은 양 끝과 가운데 **셋**만 찍는다(`08.01 · 08.12 · 08.19`). */
const labelVisible = (index: number, count: number): boolean =>
  index === 0 || index === count - 1 || index === Math.floor((count - 1) / 2);

/**
 * 잰 날들 — 시안(`1016:10419`)은 회차와 회차를 **선으로 잇는다**(`08.01 → 08.12 → 08.19`).
 * 사이에 안 잰 날이 있어도 끊지 않는다: x축이 날짜 비례라 점 사이 간격이 "그동안 안
 * 쟀다"를 이미 말하고, 0으로 채우지도 않는다(결측일에 점이 없다).
 */
const measured = (series: TrendSeries): { i: number; val: number }[] =>
  series.points.flatMap((point, i) =>
    point.val === null ? [] : [{ i, val: point.val }],
  );

const pt = (i: number, val: number, series: TrendSeries): string =>
  `${xAt(i, series.points.length).toFixed(1)} ${yAt(val, series.scale.max).toFixed(1)}`;

/** 선과 면적 — 잰 점들을 한 번에 잇고, 면적은 0선까지 내려 `Z`로 닫는다. */
const buildPaths = (series: TrendSeries): { line: string; area: string } => {
  const base = yAt(0, series.scale.max).toFixed(1);
  const x = (i: number) => xAt(i, series.points.length).toFixed(1);
  const run = measured(series);
  if (run.length === 0) {
    return { line: '', area: '' };
  }
  const top = run.map(({ i, val }) => `L${pt(i, val, series)}`).join(' ');
  return {
    line: run
      .map(({ i, val }, k) => `${k ? 'L' : 'M'}${pt(i, val, series)}`)
      .join(' '),
    area: `M${x(run[0].i)} ${base} ${top} L${x(run[run.length - 1].i)} ${base} Z`,
  };
};

const Plot = ({ series, alt }: { series: TrendSeries; alt: string }) => {
  const paths = buildPaths(series);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mt-6 w-full"
      role="img"
      aria-label={alt}
    >
      {/* 눈금은 시안대로 100 · 50 · 0 셋 — 12px 글자, BK_600, 얇은 회색 줄. */}
      {GRID_TICKS.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.l}
            x2={W - PAD.r}
            y1={yAt(tick, series.scale.max)}
            y2={yAt(tick, series.scale.max)}
            stroke="var(--bk-100)"
            strokeWidth={1}
          />
          <text
            x={PAD.l - 20}
            y={yAt(tick, series.scale.max) + 4}
            textAnchor="end"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {tick}
          </text>
        </g>
      ))}

      {/* 면적이 선보다 먼저다 — SVG는 그린 순서대로 쌓인다. 연분홍 면(시안 8%). */}
      <path
        data-testid="trend-area"
        d={paths.area}
        fill="hsl(var(--primary) / 0.08)"
      />
      <path
        data-testid="trend-line"
        d={paths.line}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* 점은 선이 아니라 포인트에서 나온다 — 앞뒤가 다 빈 하루도 마커는 찍힌다. 8px 원. */}
      {series.points.map(
        (point, i) =>
          point.val !== null && (
            <circle
              key={point.dayStart}
              cx={xAt(i, series.points.length)}
              cy={yAt(point.val, series.scale.max)}
              r={4}
              fill="hsl(var(--primary))"
            />
          ),
      )}

      {/* 30일 창에서는 날짜를 다 찍으면 글자가 겹쳐 뭉갠다 — 일정 간격으로 솎되 양 끝은
        언제나 남긴다. 축이 어디서 시작해 어디서 끝나는지가 안 읽히면 창을 바꾼 것이
        화면에 안 보인다. */}
      {series.points.map((point, i) =>
        labelVisible(i, series.points.length) ? (
          <text
            key={point.dayStart}
            x={xAt(i, series.points.length)}
            y={H - 4}
            textAnchor="middle"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {point.label}
          </text>
        ) : null,
      )}
    </svg>
  );
};

export const TrendChart = ({ records, now }: TrendChartProps) => {
  const { t } = useTranslation();
  const [windowDays, setWindowDays] = useState<TrendWindow>(TREND_DAYS);
  const series = useMemo(
    () => buildTrendSeries({ records, axis: 'overall', now, windowDays }),
    [records, now, windowDays],
  );

  /**
   * 시안 §7.1의 `종합 점수 78 / 100`과 `4점 향상` 배지.
   *
   * 향상 폭은 **창 안의 첫 값과 마지막 값**의 차다 — 직전 회차 대비가 아니다. 이 카드가
   * 말하는 것이 "요즘 어떤가"라서, 하루 못한 회차 때문에 `-9점`이 뜨면 카드 제목과 숫자가
   * 서로 다른 이야기를 한다. 값이 하나뿐이면 견줄 것이 없어 배지를 안 그린다.
   */
  const { first, latest } = series;
  const delta =
    first && latest && series.filled >= MIN_TREND_POINTS
      ? latest.val - first.val
      : null;

  const label = t('web.home.recent.overall');
  const alt = latest
    ? t('web.home.trend.alt', {
        axis: label,
        date: latest.label,
        value: t('web.home.recent.score', { value: Math.round(latest.val) }),
      })
    : t('web.home.trend.altEmpty', { axis: label });

  return (
    <section className="mt-8">
      <SectionHeadCard
        title={t('web.home.trend.section')}
        right={
          // 시안의 `최근 7일 ∨`. 고를 것이 있으므로 진짜 셀렉트다 — 글자와 화살표만 남기고
          // 브라우저 크롬은 지운다.
          <label className="relative flex min-h-[44px] items-center text-[15px] tracking-[-0.075px] text-bk-600">
            <span className="sr-only">{t('web.home.trend.periodLabel')}</span>
            <select
              value={windowDays}
              onChange={(event) =>
                setWindowDays(Number(event.target.value) as TrendWindow)
              }
              className="appearance-none bg-transparent pr-6 text-right text-[15px] text-bk-600 outline-none"
            >
              {TREND_WINDOWS.map((days) => (
                <option key={days} value={days}>
                  {t('web.home.trend.period', { value: days })}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-0 h-5 w-5 text-bk-600"
              aria-hidden="true"
            />
          </label>
        }
      />

      {/* 종합 점수 + 향상 폭(시안 §7.1) — 카드 밖 `#fafafa` 위. 숫자 위에 축 이름을
          세운다 — 숫자만 두면 78이 무엇의 78인지 알 수 없다. */}
      {latest && (
        <div className="mt-4 flex flex-col gap-3.5 px-[18px] py-4">
          <p className="text-sm tracking-[-0.07px] text-bk-600">{label}</p>
          <ScoreHero score={latest.val} tone="primary" />
          {delta !== null && delta !== 0 && (
            <span
              data-testid="trend-delta"
              className={cn(
                'inline-block self-start rounded-full px-3 py-1.5 text-[13px] font-semibold tracking-[-0.065px]',
                delta > 0
                  ? 'bg-[rgba(255,45,85,0.08)] text-pink'
                  : 'bg-bk-50 text-bk-600',
              )}
            >
              {t(
                delta > 0
                  ? 'web.home.trend.deltaUp'
                  : 'web.home.trend.deltaDown',
                { value: Math.abs(Math.round(delta)) },
              )}
            </span>
          )}
        </div>
      )}

      {series.filled < MIN_TREND_POINTS ? (
        <p className="mt-6 text-center text-sm text-bk-600">
          {t('web.home.trend.empty')}
        </p>
      ) : (
        <Plot series={series} alt={alt} />
      )}
    </section>
  );
};
